import Flickity from 'flickity';
import { debounce } from '../../scripts/utils';

/**
 *  @class
 *  @function Carousel
 */
if (!customElements.get('flickity-carousel')) {
  class FlickityCarousel extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      const carousel = this;
      this.draggable = !this.hasAttribute('not-draggable');
      let prevButtons = carousel.querySelectorAll('.flickity-prev'),
        nextButtons = carousel.querySelectorAll('.flickity-next'),
        rightToLeft = document.dir === 'rtl',
        args = {
          wrapAround: false,
          cellAlign: 'left',
          pageDots: false,
          contain: true,
          fade: false,
          autoPlay: false,
          rightToLeft: rightToLeft,
          prevNextButtons: false,
          arrowShape: '',
          percentPosition: false,
          cellSelector: '.carousel__slide',
          groupCells: false,
          draggable: this.draggable,
          initialIndex: 0,
          on: {},
        };
      this.paused = false;

      // Initiate
      let flkty;
      setTimeout(() => {
        flkty = new Flickity(carousel, args);
        this.flickityInstance = flkty;
        // Hide arrows if slides <= columns
        const numberOfColumns = parseInt(getComputedStyle(carousel).getPropertyValue('--rich-columns'));
        const totalSlides = flkty?.slides ? flkty.slides.length : 0;
        const isSliderInProductCard = this.closest('product-card') ? true : false;

        if (totalSlides <= numberOfColumns) {
          prevButtons.forEach((btn) => (btn.style.display = 'none'));
          nextButtons.forEach((btn) => (btn.style.display = 'none'));
          this.flickityInstance.slides.forEach((slide) => {
            if (window.innerWidth <= 768 && totalSlides > 1) slide.cells[0].element.style.width = isSliderInProductCard ? '100%' : '90%';
          });
        } else {
          this.flickityInstance.slides.forEach((slide) => {
            if (window.innerWidth <= 768) {
              slide.cells[0].element.style.width = isSliderInProductCard ? '100%' : '90%';
            } else {
              const newWidth = `${(this.flickityInstance.size.width / numberOfColumns) * 0.9}px`;
              slide.cells[0].element.style.width = newWidth;
            }
          });
          // Arrows
          if (prevButtons) {
            prevButtons.forEach(function (prevButton) {
              if (prevButton.dataset.flkBound === '1') return;
              prevButton.dataset.flkBound = '1';
              prevButton.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                if (this.flickityInstance) {
                  this.flickityInstance.previous();
                }
              });
            }.bind(this));

            nextButtons.forEach(function (nextButton) {
              if (nextButton.dataset.flkBound === '1') return;
              nextButton.dataset.flkBound = '1';
              nextButton.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                if (this.flickityInstance) {
                  this.flickityInstance.next();
                }
              });
            }.bind(this));
          }
        }
        flkty.resize();
        if (prevButtons && nextButtons && flkty) {
          // Hide the first arrow at the beginning
          prevButtons.forEach(function (prevButton) {
            prevButton.classList.remove('opacity-on-hover');
          });

          // Function to update arrow visibility based on screen size
          function updateArrowVisibility() {
            const lastIndex = totalSlides > 0 ? totalSlides - 1 : 0;
            // Update visibility of previous buttons
            prevButtons.forEach(function (prevButton) {
              if (prevButton) {
                if (flkty.selectedIndex === 0) {
                  prevButton.classList.remove('opacity-on-hover');
                } else {
                  prevButton.classList.add('opacity-on-hover');
                }
              }
            });

            // Update visibility of next buttons
            nextButtons.forEach(function (nextButton) {
              if (nextButton) {
                if ((flkty.selectedIndex + (numberOfColumns - 1)) >= lastIndex) {
                  nextButton.classList.remove('opacity-on-hover');
                } else {
                  nextButton.classList.add('opacity-on-hover');
                }
              }
            });
          }

          // Initial update on load
          updateArrowVisibility();

          // Update arrow visibility on slide change
          flkty.on('select', updateArrowVisibility);

          // Update arrow visibility on window resize
          window.addEventListener('resize', debounce(() => {
            updateArrowVisibility();
            this.updateSlidesWidth();
          }, 50));

        }

        this.validateDraggable();
        this.flickityInstance.on('resize', this.validateDraggable.bind(this));

        // Vertical scroll fix: Disable horizontal scrolling when scrolling vertically
        let startX = 0;
        let startY = 0;
        let isVerticalScroll = false;
        let isHorizontalScroll = false;

        carousel.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          isVerticalScroll = false;
          isHorizontalScroll = false;
        });

        carousel.addEventListener(
          'touchmove',
          (e) => {
            const moveX = Math.abs(e.touches[0].clientX - startX);
            const moveY = Math.abs(e.touches[0].clientY - startY);

            // Determine scroll direction on first movement
            if (!isVerticalScroll && !isHorizontalScroll) {
              if (moveY > moveX) {
                isVerticalScroll = true;
                flkty.options.draggable = false;
                flkty.updateDraggable();
              } else if (moveX > moveY) {
                isHorizontalScroll = true;
              }
            }

            // Prevent vertical scroll when swiping horizontally
            if (isHorizontalScroll) {
              e.preventDefault();
            }
          },
          { passive: false }
        );

        carousel.addEventListener('touchend', () => {
          if (isVerticalScroll) {
            flkty.options.draggable = true;
            flkty.updateDraggable();
          }
          isVerticalScroll = false;
          isHorizontalScroll = false;
        });
      }, 500);
    }

    validateDraggable() {
      const flkty = this.flickityInstance;
      if (!flkty || typeof flkty.updateDraggable !== 'function') return;

      const totalSlides = flkty.cells.length;
      const visibleSlides = flkty.selectedElements ? flkty.selectedElements.length : 0;

      // Disable dragging if all slides fit within carousel width
      const shouldBeDraggable = this.draggable && visibleSlides < totalSlides;

      if (flkty.options.draggable !== shouldBeDraggable) {
        flkty.options.draggable = shouldBeDraggable;
        flkty.updateDraggable();
      }
    }
    updateSlidesWidth() {
      const flkty = this.flickityInstance;
      if (!flkty) return;
      const numberOfColumns = parseInt(getComputedStyle(this).getPropertyValue('--rich-columns'));
      if (!numberOfColumns) return;
      const isSliderInProductCard = this.closest('product-card') ? true : false;
      flkty.slides.forEach((slide) => {
        if (window.innerWidth <= 768) {
          slide.cells[0].element.style.width = isSliderInProductCard ? '100%' : '90%';
        } else {
          const newWidth = `${(flkty.size.width / numberOfColumns) * 0.9}px`;
          slide.cells[0].element.style.width = newWidth;
        }
      });
      flkty.resize();
    }
  }
  customElements.define('flickity-carousel', FlickityCarousel);
}
